import { useEffect, useMemo } from 'react'
import { Controller, useWatch, type Control, type UseFormSetError } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { RadioButton } from 'primereact/radiobutton'
import { z } from 'zod'
import Button from '../../../components/ui/Button'
import FormField from '../../../components/ui/FormField'
import FormTextareaField from '../../../components/ui/form/FormTextareaField'
import SectionCard from '../../../components/ui/SectionCard'
import { useToast } from '../../../components/ui/ToastProvider'
import { getApiErrorCode, getApiErrorField } from '../../../api/apiError'
import { COLUMN_BYTES } from '../../../lib/columnBytes'
import { formatDateTime } from '../../../lib/datetime'
import { resolveFieldError } from '../../../lib/forms/messages'
import { optionalText } from '../../../lib/forms/rules'
import { useZodForm } from '../../../lib/forms/useZodForm'
import { useRespondAgenda } from '../hooks/useAgendas'
import {
  personName,
  sicilKey,
  type AgendaAnswer,
  type AgendaParticipant,
  type AgendaPersonRef,
  type MyParticipation,
} from '../types'
import ResponseBadge from './ResponseBadge'
import SubstituteField from './SubstituteField'

/** Mazeret üst sınırı (karakter). */
const REASON_MAX = 500

const SUBSTITUTE_INPUT_ID = 'agenda-substitute'

const NO_PARTICIPANTS: AgendaParticipant[] = []

const OPTIONS: { id: AgendaAnswer; labelKey: string }[] = [
  { id: 'ATTENDING', labelKey: 'agenda.status.attending' },
  { id: 'NOT_ATTENDING', labelKey: 'agenda.status.notAttending' },
]

const personRefSchema = z.object({
  employeeId: z.string(),
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
})

/**
 * Seçenek zorunlu; Katılmayacağım'da mazeret ve yerime katılacak kişi zorunlu (131 — mazeret kırpılarak;
 * sunucu da denetler).
 */
const responseSchema = z
  .object({
    response: z.enum(['ATTENDING', 'NOT_ATTENDING']).nullable(),
    reason: optionalText(REASON_MAX, COLUMN_BYTES.agendaNonParticipationReason),
    substitute: personRefSchema.nullable(),
  })
  .superRefine((v, ctx) => {
    if (v.response == null) {
      ctx.addIssue({ code: 'custom', path: ['response'], message: 'validation.required' })
    }
    if (v.response !== 'NOT_ATTENDING') {
      return
    }
    if (v.substitute == null) {
      ctx.addIssue({ code: 'custom', path: ['substitute'], message: 'agenda.validation.substituteRequired' })
    }
    if (v.reason === '') {
      ctx.addIssue({ code: 'custom', path: ['reason'], message: 'agenda.validation.excuseRequired' })
    }
  })

type ResponseFormValues = z.infer<typeof responseSchema>
type ResponseControl = Control<ResponseFormValues, unknown, ResponseFormValues>

function toFormValues(p: MyParticipation): ResponseFormValues {
  return {
    // "Yanıt Bekleniyor" bir seçenek değildir → seçim boş başlar.
    response: p.response === 'PENDING' ? null : p.response,
    reason: p.reason ?? '',
    // Zaman değişince yanıt sıfırlanır ama yerime katılacak kişi kalır — form onunla dolu gelir (131).
    substitute: p.substitute ?? null,
  }
}

/** Sunucu alan hatası → form alanı; eşlenmezse `false` (çağıran genel hata gösterir). */
function applyServerError(err: unknown, setError: UseFormSetError<ResponseFormValues>): boolean {
  const field = getApiErrorField(err)
  if (field === 'reason') {
    setError('reason', { type: 'server', message: 'agenda.validation.excuseRequired' })
    return true
  }
  if (field === 'substituteEmployeeId') {
    setError('substitute', { type: 'server', message: 'agenda.validation.substituteInvalid' })
    return true
  }
  if (field === 'response') {
    setError('response', { type: 'server', message: 'validation.invalid' })
    return true
  }
  return false
}

interface Props {
  agendaId: number
  participation: MyParticipation
  /** Toplantı tekrarlı bir seriye bağlı mı (126): yanıt serinin başlamamış tüm toplantılarına yazılır. */
  recurring?: boolean
  /** Davetliler (131): yerime katılacak kişi önerilerinden düşülür — davette olan seçilemez. */
  participants?: AgendaParticipant[]
  /** Toplantıyı oluşturanın sicili (131, 2026-11-03): o da toplantıdadır, önerilerden düşülür. */
  ownerEmployeeId?: string | null
}

/**
 * Katılımcının kendi yanıtı. Değiştirilebilirliği sunucunun `respondable` bayrağı söyler ("şimdi"
 * istemcide hesaplanmaz); `false` ise panel salt okunurdur. Başkasının yerine katılan kişinin yanıtı
 * sabittir (131) — panel bunu ve kimin yerine olduğunu söyler.
 */
export default function ResponsePanel({
  agendaId,
  participation,
  recurring = false,
  participants = NO_PARTICIPANTS,
  ownerEmployeeId = null,
}: Readonly<Props>) {
  const { t } = useTranslation()
  return (
    <SectionCard title={t('agenda.response.title')} className="agenda-response">
      <p className="agenda-response-current">
        <span>{t('agenda.response.current')}:</span> <ResponseBadge response={participation.response} />
        {participation.responseTime && (
          <span className="agenda-muted"> {formatDateTime(participation.responseTime)}</span>
        )}
      </p>
      {/* Sunucu hâli, form seçimi değil (131 FR-011): düzenleyen saati değiştirip yanıt "Yanıt Bekleniyor"a
          döndüğünde de yerime katılan kişi davette "Katılacağım"dır — davetli bunu görmeli. */}
      {participation.substitute && (
        <p className="agenda-excuse">
          <span className="agenda-muted">{t('agenda.response.substitute')}: </span>
          {personName(participation.substitute)}
        </p>
      )}
      <PanelBody
        agendaId={agendaId}
        participation={participation}
        recurring={recurring}
        participants={participants}
        ownerEmployeeId={ownerEmployeeId}
      />
    </SectionCard>
  )
}

function PanelBody({
  agendaId,
  participation,
  recurring,
  participants,
  ownerEmployeeId,
}: Readonly<Required<Props>>) {
  if (participation.substituteFor) {
    return <LockedSubstitute original={participation.substituteFor} />
  }
  if (!participation.respondable) {
    return <ReadOnlyResponse participation={participation} />
  }
  return (
    <ResponseForm
      agendaId={agendaId}
      participation={participation}
      recurring={recurring}
      participants={participants}
      ownerEmployeeId={ownerEmployeeId}
    />
  )
}

/** Başkasının yerine katılan kişi (131): yanıtı "Katılacağım" olarak sabittir. */
function LockedSubstitute({ original }: Readonly<{ original: AgendaPersonRef }>) {
  const { t } = useTranslation()
  return (
    <p className="agenda-muted" role="note">
      {t('agenda.response.lockedSubstitute', { name: personName(original) })}
    </p>
  )
}

/** Toplantı başladı: yanıt ve mazeret yalnız gösterilir (yerime katılacak kişi durum satırında). */
function ReadOnlyResponse({ participation }: Readonly<{ participation: MyParticipation }>) {
  const { t } = useTranslation()
  return (
    <>
      {participation.reason && (
        <p className="agenda-excuse">
          <span className="agenda-muted">{t('agenda.response.excuse')}: </span>
          {participation.reason}
        </p>
      )}
      <p className="agenda-muted" role="note">
        {t('agenda.response.closed')}
      </p>
    </>
  )
}

function ResponseForm({
  agendaId,
  participation,
  recurring,
  participants,
  ownerEmployeeId,
}: Readonly<Required<Props>>) {
  const { t } = useTranslation()
  const toast = useToast()
  const respond = useRespondAgenda(agendaId)
  const { control, handleSubmit, reset, setError } = useZodForm(responseSchema, {
    defaultValues: toFormValues(participation),
  })
  const selected = useWatch({ control, name: 'response' })
  // Davette olan herkes (görüntüleyen dahil) ve toplantıyı oluşturan önerilmez; sunucu da reddeder. Mevcut
  // vekil hariç (131 FR-003): çipi kaldırılsa da aynı kişi yeniden seçilebilmeli — sunucu onu kabul eder.
  const currentSubstitute = participation.substitute?.employeeId
  const exclude = useMemo(() => {
    const keep = currentSubstitute ? sicilKey(currentSubstitute) : null
    const inMeeting = participants.map((p) => sicilKey(p.employeeId)).filter((key) => key !== keep)
    if (ownerEmployeeId) inMeeting.push(sicilKey(ownerEmployeeId))
    return new Set(inMeeting)
  }, [participants, currentSubstitute, ownerEmployeeId])

  // Sunucudan yeni hâl geldiğinde (kayıt, 409 sonrası tazeleme, sahibin sıfırlaması) form o hâle
  // döner — eski seçim yeni damgayla gönderilip sıfırlamayı ezmesin.
  useEffect(() => {
    reset(toFormValues(participation))
  }, [participation, reset])

  const onError = (err: unknown) => {
    if (getApiErrorCode(err) === 'INVALID_STATE') {
      toast.error(t('agenda.response.closedError'))
      return
    }
    if (getApiErrorCode(err) !== 'VALIDATION_ERROR') return // merkezî bildirim (409 dahil)
    if (!applyServerError(err, setError)) toast.error(t('common.errors.generic'))
  }

  const onValid = (v: ResponseFormValues) => {
    const notAttending = v.response === 'NOT_ATTENDING'
    respond.mutate(
      {
        response: notAttending ? 'NOT_ATTENDING' : 'ATTENDING',
        // Katılacağım'da mazeret ve yerime katılacak kişi gönderilmez: önceki seçim geçerli yanıtın parçası değildir.
        reason: notAttending ? v.reason : null,
        substituteEmployeeId: notAttending ? (v.substitute?.employeeId ?? null) : null,
        modDate: participation.modDate,
      },
      {
        onSuccess: () =>
          toast.success(t(recurring ? 'agenda.response.savedSeries' : 'agenda.response.saved')),
        onError,
      },
    )
  }

  return (
    <div className="agenda-response-form">
      <ResponseOptions control={control} t={t} disabled={respond.isPending} />
      {recurring && <p className="agenda-muted agenda-recurrence-hint">{t('agenda.response.seriesHint')}</p>}
      {selected === 'NOT_ATTENDING' && (
        <>
          <SubstituteControl control={control} t={t} disabled={respond.isPending} exclude={exclude} />
          <FormTextareaField
            control={control}
            name="reason"
            label={t('agenda.response.excuse')}
            required
            maxLength={REASON_MAX}
            placeholder={t('agenda.response.excusePlaceholder')}
            disabled={respond.isPending}
          />
        </>
      )}
      <div className="agenda-response-actions">
        <Button
          label={t('agenda.response.save')}
          loading={respond.isPending}
          disabled={respond.isPending}
          onClick={handleSubmit(onValid)}
        />
      </div>
    </div>
  )
}

/** "Yerime Katılacak Kişi" (131) — Katılmayacağım'da zorunlu; seçilen kişi davete eklenir. */
function SubstituteControl({
  control,
  t,
  disabled,
  exclude,
}: Readonly<{ control: ResponseControl; t: TFunction; disabled: boolean; exclude: ReadonlySet<string> }>) {
  return (
    <Controller
      control={control}
      name="substitute"
      render={({ field, fieldState }) => (
        <FormField
          label={t('agenda.response.substitute')}
          htmlFor={SUBSTITUTE_INPUT_ID}
          required
          full
          error={resolveFieldError(fieldState.error?.message, t)}
        >
          <SubstituteField
            inputId={SUBSTITUTE_INPUT_ID}
            value={field.value}
            onChange={field.onChange}
            exclude={exclude}
            placeholder={t('agenda.response.substitutePlaceholder')}
            invalid={!!fieldState.error}
            disabled={disabled}
            describedBy={`${SUBSTITUTE_INPUT_ID}-err`}
          />
          <p className="agenda-muted agenda-field-hint">{t('agenda.response.substituteHint')}</p>
        </FormField>
      )}
    />
  )
}

/** İki seçenekli radyo grubu; hata metni paylaşılan alan hata kutusu sınıfıyla gösterilir. */
function ResponseOptions({
  control,
  t,
  disabled,
}: Readonly<{ control: ResponseControl; t: TFunction; disabled: boolean }>) {
  return (
    <Controller
      control={control}
      name="response"
      render={({ field, fieldState }) => (
        <div className="ui-field">
          <span className="ui-field-label" id="agenda-response-label">
            {t('agenda.response.question')} <span className="ui-req">*</span>
          </span>
          <div
            className="agenda-response-options"
            role="radiogroup"
            aria-labelledby="agenda-response-label"
            aria-describedby="agenda-response-err"
          >
            {OPTIONS.map((o) => (
              <div key={o.id} className="agenda-response-option">
                <RadioButton
                  inputId={`agenda-response-${o.id}`}
                  name="agendaResponse"
                  checked={field.value === o.id}
                  disabled={disabled}
                  onChange={() => field.onChange(o.id)}
                />
                <label htmlFor={`agenda-response-${o.id}`}>{t(o.labelKey)}</label>
              </div>
            ))}
          </div>
          <div className="ui-field-err" id="agenda-response-err" aria-live="polite">
            {resolveFieldError(fieldState.error?.message, t)}
          </div>
        </div>
      )}
    />
  )
}
